import React, { useRef, useEffect, useState } from "react";
import { useReactToPrint } from "react-to-print";
import { withSidebar } from "../hoc/withSidebar";
import { useTranslation } from "react-i18next";
import ERPSBaseButton from "../custom/inputs/ERPSBaseButton";
import { useDispatch, useSelector } from "react-redux";
import Breadcrumb from "../Breadcrumb/Breadcrumb";
import DialogHeader from "../custom/inputs/ERPSBaseModelDialog";
import actions from "../../actions";
import ConfirmationMessage from "../custom/containers/ConfirmationMesssage";
import ErrorMessage from "../custom/containers/ErrorMessage";
import ERPSBaseTextField from "../custom/inputs/ERPSBaseTextField";
import ERPSTransactionDetails from "../custom/ERPSTransactionDetails";
import { Regex }  from "../../constants/sharedConstants.js";
import { listProps, checkFieldValue, checkLengthAndRegex } from "../../utils/helper";
import { isBillerUser } from "../../utils/bpportal-common-utils";
import { Dialog, DialogActions, DialogContent, HStack, ResponsiveBox, Stack, Typography } from "../primitives";

const RejectPaymentDetails = (props) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const {
        location: { state },
    } = props;

    const [isOpenDialog, setIsOpenDialog] = useState(false);
    const [isRejected, setIsRejected] = useState(false);
    const [rejectReasonError, setRejectReasonError] = useState("");

    const transactionForRejectData = useSelector(
        (state) => state.transactionEnquiryReducer?.transactionForRejectData?.data
    );
    const successMessage = useSelector(
        (state) => state.transactionEnquiryReducer?.rejectTransactionData
    );
    const [errorMessage, setErrorMessage] = useState(null);
    const rejectErrorMessage = useSelector(
        (state) => state.transactionEnquiryReducer?.rejectTransactionError
    );

    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [loading, setLoading] = useState(false);

    const user = useSelector((state) => state.loginUserReducer.user);
    const [isBillerRole] = useState( isBillerUser(user.role?.role_type) );

    const [data] = useState( transactionForRejectData?.find(({ payment_id }) => payment_id === state.id) || {} );
   
    const [isRejectionAuthorized] = useState( (!isBillerRole || data?.returns_enabled_txn === "Y" || data?.returns_enabled_biller === "Y") );

    const errorRef = useRef(null);
    const successRef = useRef();
    const printComponentRef = useRef();

    const [rejectReason, setRejectReason] = useState('');

    useEffect(() => {
        if (showSuccessMessage) {
            setIsRejected(true);
            if (successRef.current) {
                successRef.current.focus();
            }
        }
    }, [showSuccessMessage]);

    useEffect(() => {
            localStorage.setItem('rejectReasonValue', rejectReason);
    }, [rejectReason]);

    useEffect(() => {
        setErrorMessage(rejectErrorMessage);
        if (rejectErrorMessage && errorRef?.current) {
            errorRef.current.focus();
        }
    }, [rejectErrorMessage]);

    useEffect(() => {
        dispatch({ type: "UPDATE_TRANSACTION_FOR_REJECT_SUCCESS", data: null })
        const savedValue = localStorage.getItem('rejectReasonValue');
        if (savedValue)
            setRejectReason(savedValue);

        if (!isRejectionAuthorized)
            setErrorMessage(t("erps.rejectpaymentmerchants.message.rejectionNotAuthorizedByPayorFI"));

        return onExit;  // clean up the ref on exit
    }, []);

    const onExit = () => {
        errorRef.current = null;
        successRef.current = null;
        printComponentRef.current = null;
    }

    const handleReject = async  () => {
        setIsOpenDialog(false);
        setLoading(true);
        if (showSuccessMessage) {
            setShowSuccessMessage(false);
        }
        let reqBodyForReject = {};
        if (data) {
            reqBodyForReject = {
                correlation_id: data.id,
                fi_number: data.fi_number,
                payment_date: data.payment_date,
                recall_reject_reason: rejectReason,
                recalled_rejected_by: user?.userId,
                language_code: user?.languageCode,
                role_type: user.role?.role_type,
                creditor_fi_number: user.searchBillerFilesDto?.creditor_fi_number
            };
        }

        dispatch(
            actions.transactionEnquiryActions.rejectTransaction(
                {  reqBodyForReject },
                { setShowSuccessMessage, setLoading }
            )
        );
    };

    const handleChange = (e) => {
        const value = e.target.value;
        let errMsg = checkLengthAndRegex(value, "erps.rejectpaymentmerchants.page.label.reject.reason", 80, Regex.regularText);
        // one invalid character will show in the field, but further input isn't allowed until the invalid character is removed
        setRejectReasonError(errMsg);
        if ("" === errMsg || Regex.regularText.test(rejectReason))  // if either current value is valid or previous value is valid
            setRejectReason(value);
    };

    const handleConfirmReject = () => {
        if (validateRejectReason()) {
            setErrorMessage(null);
            setIsOpenDialog(true);
        }
    };

    const validateRejectReason = () => {
        let errorMsg = checkFieldValue(rejectReason, "erps.rejectpaymentmerchants.page.label.reject.reason", true, (v) => Regex.regularText.test(v));
        setRejectReasonError(errorMsg);
        return "" === errorMsg;
    }

    const handlePrint = useReactToPrint({
        content: () => printComponentRef.current
    });
    const detailArguments = successMessage ? {data: data, rejectReason: rejectReason, rejectedBy: user?.userId} : {data: data};

    return (
        <div role="main">
            {successMessage && showSuccessMessage && (
                <ConfirmationMessage
                    message={successMessage}
                    setShowMessage={setShowSuccessMessage}
                    inputRef={successRef}
                />
            )}
            <ErrorMessage 
              error={errorMessage}
              errorRef={errorRef}
              mt={2}
            />
            <ResponsiveBox sx={{ ml: 3 }}>
                <Breadcrumb {...props} fromDetailPage={true}/>
            </ResponsiveBox>

            <ERPSTransactionDetails {...detailArguments} ref={printComponentRef}/>

            <ResponsiveBox id={"screenshot-element"} sx={{ ml: 3 }}>
                {!successMessage && (
                <ResponsiveBox>
                    <ERPSBaseTextField
                        id="rejectReason"
                        type="text"
                        label={t("erps.rejectpaymentmerchants.page.label.reject.reason")}
                        className="transactionEnquiry__textField__width"
                        size="small"
                        onChange={handleChange}
                        value={rejectReason}
                        required="true"
                        inputProps={{ maxLength: 80 }}
                        error={!!rejectReasonError}
                        helperText={rejectReasonError}
                        isDisabled={!isRejectionAuthorized}
                    />
                </ResponsiveBox>
                )}
            </ResponsiveBox>
            <HStack spacing={2} ml={3}>
                <ERPSBaseButton
                    variant="secondary"
                    className="button_with_margin48"
                    label={t("erps.newuser.button.back", "Back")}
                    onClick={() =>
                        props.history.push("/reject-payments-merchants/", {
                            fromDetailPage: true,
                            isRejected : isRejected
                        })
                    }
                />

                {!successMessage ? (
                    <ERPSBaseButton
                        variant="primary"
                        className="button_with_margin48"
                        label={t("erps.rejectpaymentmerchants.page.label.confirm.reject")}
                        onClick={handleConfirmReject}
                        loading={loading}
                        isDisabled={isRejected || !isRejectionAuthorized}
                    />
                ) : (
                    //Logic needs to be added for print button
                    <ERPSBaseButton
                        variant="primary"
                        className="button_with_margin48"
                        label={t("erps.transactionEnquiry.page.transactionDetails.button.print")}
                        onClick={handlePrint}
                        loading={loading}
                    />
                )}

            </HStack>

            <Dialog
                open={isOpenDialog}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                fullWidth={true}
            >
                <DialogHeader
                    id="alert-dialog-title"
                    onClose={() => setIsOpenDialog(false)}
                    className={"box-dialog-header"}
                >
                    {t("erps.common.label.confirmation")}
                </DialogHeader>
                <DialogContent
                    id="alert-dialog-description"
                    className={"dialog-content-size"}
                >
                    {t("erps.rejectpaymentmerchants.confirm.reject.transaction")}
                </DialogContent>
                <DialogActions className={"box-dialog-buttons"}>
                    <ERPSBaseButton
                        variant="secondary"
                        className="button_with_zeromargin"
                        onClick={() => setIsOpenDialog(false)}
                        label={t("erps.common.button.cancel")}
                    />
                    <ERPSBaseButton
                        variant="primary"
                        className="button_with_zeromargin"
                        onClick={handleReject}
                        label={t("erps.welcome.page.button.Ok")}
                    />
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default withSidebar(RejectPaymentDetails);
